import { useEffect } from 'react';

/**
 * GlobalErrorFocusProvider - Automatically handles error focus for all pages
 * 
 * This component monitors the entire application for form validation errors
 * and automatically focuses the first error field when errors appear.
 * 
 * Benefits:
 * - WCAG 2.4.3 (Focus Order) compliance - prevents focusing on static error text
 * - WCAG 3.3.1 (Error Identification) best practices
 * - Works globally without requiring individual pages to implement the hook
 * - Improves keyboard navigation and screen reader accessibility
 * 
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components to render
 * @param {boolean} props.enabled - Whether global error focusing is enabled (default: true)
 * @param {number} props.debounceDelay - Delay before focusing to allow DOM updates (default: 100ms)
 * @param {string} props.errorSelector - Custom CSS selector for error fields
 * @param {boolean} props.scrollToTop - Whether to scroll to top of page on error (default: true)
 * @param {boolean} props.scrollToField - Whether to scroll to the error field instead of top (default: false)
 * @param {string} props.scrollBehavior - Scroll behavior: 'smooth' or 'auto' (default: 'smooth')
 * @param {number} props.scrollOffset - Offset in pixels from top when scrolling to field (default: 100)
 */
const GlobalErrorFocusProvider = ({ 
  children, 
  enabled = true,
  debounceDelay = 100,
  errorSelector = null,
  scrollToTop = true,
  scrollToField = false,
  scrollBehavior = 'smooth',
  scrollOffset = 100
}) => {
  useEffect(() => {
    if (!enabled) return;

    let timeoutId;
    let focusedErrorFields = new Set(); // Track which error fields we've already focused
    
    // Default selector for error fields - covers MUI error states and ARIA invalid
    const defaultSelector = 
      '.Mui-error input:not([type="hidden"]), ' +
      '.Mui-error textarea, ' +
      '.Mui-error select, ' +
      'input[aria-invalid="true"]:not([type="hidden"]), ' +
      'textarea[aria-invalid="true"], ' +
      'select[aria-invalid="true"]';

    const selector = errorSelector || defaultSelector;

    /**
     * Observes DOM changes to detect when error fields appear
     * and automatically focuses the first one (only once per error field)
     */
    const observeErrorFields = () => {
      // Use MutationObserver to watch for error states being added to the DOM
      const observer = new MutationObserver((mutations) => {
        // Clear any pending timeout
        if (timeoutId) {
          clearTimeout(timeoutId);
        }

        // Debounce to avoid multiple rapid focus attempts
        timeoutId = setTimeout(() => {
          const firstErrorField = document.querySelector(selector);
          
          // If no error fields exist, reset the tracking
          if (!firstErrorField) {
            focusedErrorFields.clear();
            return;
          }
          
          // Generate a unique identifier for the error field
          const fieldId = firstErrorField.name || firstErrorField.id || firstErrorField.getAttribute('aria-label') || '';
          const fieldKey = `${fieldId}_${firstErrorField.tagName}`;
          
          // Only focus if we haven't already focused this error field
          if (!focusedErrorFields.has(fieldKey)) {
            // Check if the error field is visible
            const isVisible = firstErrorField.offsetParent !== null;
            
            if (isVisible) {
              // Scroll to top if enabled (default behavior matches useErrorFocus)
              if (scrollToTop && !scrollToField) {
                window.scrollTo({
                  top: 0,
                  left: 0,
                  behavior: scrollBehavior
                });
              }
              
              // Scroll to error field if enabled (alternative to scrollToTop)
              if (scrollToField) {
                const fieldPosition = firstErrorField.getBoundingClientRect().top + window.pageYOffset;
                const offsetPosition = fieldPosition - scrollOffset;
                
                window.scrollTo({
                  top: offsetPosition,
                  behavior: scrollBehavior
                });
              }
              
              // Focus the field
              firstErrorField.focus();
              
              // Mark this field as focused
              focusedErrorFields.add(fieldKey);
            }
          }
        }, debounceDelay);
      });

      // Observe the entire document for changes in attributes and child elements
      observer.observe(document.body, {
        attributes: true,
        attributeFilter: ['class', 'aria-invalid'],
        childList: true,
        subtree: true
      });

      return observer;
    };

    const observer = observeErrorFields();

    // Cleanup observer on unmount
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      observer.disconnect();
    };
  }, [enabled, debounceDelay, errorSelector, scrollToTop, scrollToField, scrollBehavior, scrollOffset]);

  return children;
};

export default GlobalErrorFocusProvider;
